// Phase 01.1 Plan 06：apiClient 工厂层 — Real + Mock 同文件导出（用户 D-39 决策）。
// Phase 2 真后端切换：Plan 07 render/index.vue 把 DEBUG_USE_MOCK 常量改为 false，
// createMockApiClient 走 tree-shaking 剔除。

import { BaseApiClient } from '@ark/web-api/frontend';
import {
  activityConfig,
  DRAW_STATUS,
  DELIVERY_STATUS,
  type AmoeSubmitReq,
  type AmoeSubmitRsp,
  type AmoeAvailabilityRsp,
  type SendSmsReq,
  type SendSmsRsp,
  type GetDrawResultRsp,
} from '@ark/mets-shared';

/** AMOE apiClient 抽象接口。
 *  D-39 + Critical Non-Portable Difference D-01：
 *  - 不 export 全局单例（禁止 `export const apiClient = new ...()`）
 *  - 所有 composable 接 apiClient 参数，由 render/editor 工厂注入
 *  - getDrawResult 接受 signal?: AbortSignal，匹配 Plan 04 pollDrawResult 的 AbortController（RH-B3） */
export type AmoeApiClient = {
  submit(req: AmoeSubmitReq): Promise<AmoeSubmitRsp>;
  getAvailability(seasonSign: string): Promise<AmoeAvailabilityRsp>;
  sendSms(req: SendSmsReq): Promise<SendSmsRsp>;
  /** Stage 2 轮询；signal 来自 Plan 04 useLottery 的 AbortController（RH-B3） */
  getDrawResult(
    chancePublicId: string,
    signal?: AbortSignal,
  ): Promise<GetDrawResultRsp>;
};

class RealAmoeApiClient extends BaseApiClient implements AmoeApiClient {
  constructor() {
    super({ apiPrefix: activityConfig.apiPrefix });
  }

  /** submit 写操作不重试（D-06：后端幂等不保，重复 decree 风险） */
  async submit(req: AmoeSubmitReq): Promise<AmoeSubmitRsp> {
    return await this.post<AmoeSubmitRsp>('amoe/submit', req);
  }

  async getAvailability(seasonSign: string): Promise<AmoeAvailabilityRsp> {
    return await this.get<AmoeAvailabilityRsp>('amoe/availability', {
      seasonSign,
    });
  }

  async sendSms(req: SendSmsReq): Promise<SendSmsRsp> {
    return await this.post<SendSmsRsp>('amoe/sms/send', req);
  }

  async getDrawResult(
    chancePublicId: string,
    signal?: AbortSignal,
  ): Promise<GetDrawResultRsp> {
    void signal;
    return await this.get<GetDrawResultRsp>('amoe/draw-result', {
      chancePublicId,
    });
  }
}

export function createApiClient(): AmoeApiClient {
  return new RealAmoeApiClient();
}

/* ---------------- Mock 工厂（dev 调试，非最终代码） ---------------- */
/* Phase 01.1 不走网络：render 通过 `DEBUG_USE_MOCK = true` 常量选择此工厂；
 * Phase 2 真后端切换时在 Plan 07 render/index.vue 把该常量改为 `false`。
 * 本工厂与 createApiClient 同文件导出，不再拆独立文件。 */

type MockScenario =
  | 'won'
  | 'lost'
  | 'pending'
  | 'alreadyMoomoo'
  | 'duplicate'
  | 'ipRejected'
  | 'noRound'
  | 'seasonSignMissing'
  | 'network';

const KNOWN_SCENARIOS: readonly MockScenario[] = [
  'won',
  'lost',
  'pending',
  'alreadyMoomoo',
  'duplicate',
  'ipRejected',
  'noRound',
  'seasonSignMissing',
  'network',
];

function normalizeScenario(s?: string): MockScenario {
  return (KNOWN_SCENARIOS as readonly string[]).includes(s ?? '')
    ? (s as MockScenario)
    : 'won';
}

export function createMockApiClient(scenario?: string): AmoeApiClient {
  const s = normalizeScenario(scenario);
  let pollCount = 0;

  return {
    /** RH-B2：成功主路径**仅**返 `{success:true, chancePublicId}`，不携带 drawStatus/prize */
    async submit(req: AmoeSubmitReq): Promise<AmoeSubmitRsp> {
      void req;
      await new Promise<void>((resolve) => setTimeout(resolve, 300));
      pollCount = 0;
      switch (s) {
        case 'won':
          return {
            success: true,
            chancePublicId: 'mock-chance-won',
            isNew: true,
          };
        case 'lost':
          return {
            success: true,
            chancePublicId: 'mock-chance-lost',
            isNew: true,
          };
        case 'pending':
          return {
            success: true,
            chancePublicId: 'mock-chance-pending',
            isNew: true,
          };
        // RH-B5：reason 独立字段，不塞 errorMessage
        case 'alreadyMoomoo':
          return { success: false, businessCode: 31031 };
        case 'duplicate':
          return { success: false, businessCode: 31032 };
        case 'ipRejected':
          return { success: false, businessCode: 31033 };
        case 'noRound':
          return { success: false, businessCode: 31041, reason: 'ended' };
        case 'seasonSignMissing':
          // submit 不应被点击（availability 已切空）；兜底返 no_round
          return { success: false, businessCode: 31041, reason: 'no_round' };
        case 'network':
          throw new Error('mock network error');
      }
    },

    async getAvailability(seasonSign: string): Promise<AmoeAvailabilityRsp> {
      void seasonSign;
      await new Promise<void>((resolve) => setTimeout(resolve, 150));
      if (s === 'noRound') {
        return { hasActiveRound: false, reason: 'ended' };
      }
      if (s === 'seasonSignMissing') {
        return { hasActiveRound: false, reason: 'no_round' };
      }
      return { hasActiveRound: true };
    },

    async sendSms(req: SendSmsReq): Promise<SendSmsRsp> {
      void req;
      await new Promise<void>((resolve) => setTimeout(resolve, 200));
      if (s === 'alreadyMoomoo') {
        return { sent: false, isAlreadyMoomoo: true };
      }
      return { sent: true };
    },

    async getDrawResult(
      chancePublicId: string,
      signal?: AbortSignal,
    ): Promise<GetDrawResultRsp> {
      void chancePublicId;
      void signal;
      pollCount += 1;
      await new Promise<void>((resolve) => setTimeout(resolve, 150));
      switch (s) {
        case 'won':
          return {
            success: true,
            drawStatus: DRAW_STATUS.WON,
            deliveryStatus: DELIVERY_STATUS.DELIVERED,
            prize: {
              prizeId: 1,
              prizeType: 'physical',
              prizeName: 'Samsung Galaxy Book',
            },
          };
        case 'lost':
          return {
            success: true,
            drawStatus: DRAW_STATUS.LOST,
            deliveryStatus: DELIVERY_STATUS.DELIVERED,
            prize: null,
          };
        case 'pending':
          // 前 2 次返未定，第 3 次收敛 PENDING 终态（触发 UI-SPEC pending 文案）
          if (pollCount < 3) {
            return { success: true };
          }
          return {
            success: true,
            drawStatus: DRAW_STATUS.PENDING,
            deliveryStatus: DELIVERY_STATUS.DELIVERED,
            prize: null,
          };
        default:
          // 业务错 scenario 的 submit 已返终态，getDrawResult 不应被调用；兜底给 pending 终态
          return {
            success: true,
            drawStatus: DRAW_STATUS.PENDING,
            deliveryStatus: DELIVERY_STATUS.DELIVERED,
            prize: null,
          };
      }
    },
  };
}
